<script lang="ts" setup>
	import { useDevicesList, useUserMedia } from '@vueuse/core'

	export type mediaType = {
		blob: null | Blob
		src: null | string
	}

	const props = defineProps<{
		file?: mediaType
		tip?: string
		show: boolean
	}>()
	const video = ref<HTMLVideoElement>()
	const canvas = ref<HTMLCanvasElement>()

	const currentCamera = ref('')
	const openingCamera = ref(false)
	const { enabled, stop, start, isSupported, stream } = useUserMedia({
		constraints: {
			video: {
				deviceId: currentCamera.value,
				facingMode: {
					exact: 'environment'
				}
			}
		}
	})

	const emits = defineEmits<{
		(event: 'change', param: File): void
		(event: 'update:file', param: File): void
		(event: 'not-supported'): void
		(event: 'update:show', param: boolean): void
	}>()

	const capture = async () => {
		if (!canvas.value || !video.value) {
			console.error('Failed to capture canvas')
			useNotification().alert({
				status: 400,
				message: 'Something went wrong, can not capture image.'
			})
			return null
		}
		const context = canvas.value.getContext('2d')
		// The values provided are required image style props

		context?.drawImage(video.value, 0, 0, 481, 480)

		try {
			await canvas.value.toBlob(
				blob => {
					if (!blob) {
						return null
					}
					const photo_name = Date.now()
					const fileResult = new File([blob], `${photo_name}.jpg`, {
						type: 'image/jpeg',
						lastModified: new Date().getTime()
					})
					stop()
					emits('change', fileResult)
					emits('update:file', fileResult)
					emits('update:show', false)
				},
				'image/png',
				0.7
			)
		} catch (error) {
			useNotification().alert({
				status: 400,
				message: error as string
			})
		} finally {
			openingCamera.value = false
		}
	}

	const openCamera = () => {
		openingCamera.value = true
		const { videoInputs: cameras } = useDevicesList({
			requestPermissions: true,
			constraints: {
				video: true,
				audio: false
			},
			onUpdated() {
				if (!cameras.value.find(i => i.deviceId === currentCamera.value))
					currentCamera.value = cameras.value[0]?.deviceId
			}
		})
		start()
			.finally(() => (openingCamera.value = false))
			.catch(() => {
				useNotification().alert({
					status: 400,
					message: 'Failed to open camera, please try uploading'
				})
				emits('not-supported')
			})
	}

	onUnmounted(() => {
		stop()
		enabled.value = false
	})

	watch(
		() => props.show,
		() => {
			if (!isSupported.value && props.show) {
				emits('not-supported')
				stop()
				enabled.value = false
				emits('update:show', false)

				return useNotification().alert({
					status: 400,
					message: 'Sorry your browser does not support this feature, use upload'
				})
			} else if (props.show) {
				openCamera()
			}
		}
	)
	watch(stream, media => {
		if (video.value) {
			video.value.srcObject = media!
		}
	})
</script>

<template>
	<div :class="[show ? 'visible' : 'invisible -z-40']">
		<video
			ref="video"
			class="fixed transition-all"
			:class="[show ? 'visible left-0 top-0 z-[1000] h-screen w-full bg-[rgba(0,0,0,0.8)]' : 'invisible -z-10']"
			muted
			autoplay
			playsinline
		/>

		<canvas ref="canvas" :width="481" :height="480" class="absolute left-0 top-0 w-0 opacity-0" />

		<button
			name="capture image"
			type="button"
			:disabled="openingCamera"
			:class="[show ? 'flex' : 'hidden']"
			class="fixed bottom-[10%] left-0 right-0 z-[1100] mx-auto h-[60px] w-[60px] flex-none rounded-full border-[6px] border-white bg-error-500 transition-all disabled:animate-pulse disabled:border-transparent disabled:bg-error-300"
			@click="capture"
		/>
	</div>
</template>

<style scoped>
	.spin :deep(.ant-spin-dot-item) {
		@apply bg-[#506661];
	}
</style>
